import { useEffect, useState } from "react";
import { api, MGR_ROLES, STATUS_COLORS } from "../api";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { LogIn, LogOut, MapPin } from "lucide-react";
import { toast } from "sonner";

const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString("en-MY", { hour: "2-digit", minute: "2-digit" }) : "--:--");

export default function Attendance() {
  const { user } = useAuth();
  const { lang } = useLang();
  const bm = lang === "bm";
  const [today, setToday] = useState(null);
  const [records, setRecords] = useState([]);
  const [scope, setScope] = useState("mine");
  const [now, setNow] = useState(new Date());
  const isMgr = MGR_ROLES.includes(user?.role);

  const load = () => {
    api.get("/attendance/today").then((r) => setToday(r.data?.attendance_id ? r.data : null)).catch(() => {});
    const month = new Date().toISOString().slice(0, 7);
    api.get("/attendance/records", { params: { month, scope } }).then((r) => setRecords(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [scope]);
  useEffect(() => {
    const i = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(i);
  }, []);

  const clock = async (type) => {
    try {
      await api.post("/attendance/clock", { type });
      toast.success(type === "in" ? (bm ? "Clock-in berjaya" : "Clocked in") : (bm ? "Clock-out berjaya" : "Clocked out"));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  return (
    <div data-testid="attendance-page" className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{bm ? "Kehadiran" : "Attendance"}</h1>
        {isMgr && (
          <div className="flex gap-2">
            {["mine", "all"].map((s) => (
              <Button key={s} data-testid={`attendance-scope-${s}`} size="sm" variant={scope === s ? "default" : "outline"} onClick={() => setScope(s)}>
                {s === "mine" ? (bm ? "Saya" : "Mine") : (bm ? "Semua" : "All Staff")}
              </Button>
            ))}
          </div>
        )}
      </div>

      <Card className="bg-gradient-to-br from-slate-900 to-slate-800 text-white border-slate-700">
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-center justify-between gap-6">
            <div>
              <p className="text-xs uppercase tracking-wider text-slate-400">{bm ? "Masa Sekarang (MYT)" : "Current Time (MYT)"}</p>
              <p className="text-4xl font-extrabold font-mono mt-1" data-testid="attendance-live-clock">
                {now.toLocaleTimeString("en-MY", { hour: "2-digit", minute: "2-digit", second: "2-digit", timeZone: "Asia/Kuala_Lumpur" })}
              </p>
              <p className="text-sm text-slate-400 mt-1">{now.toLocaleDateString("en-MY", { weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: "Asia/Kuala_Lumpur" })}</p>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-center">
                <p className="text-xs text-slate-400">Clock In</p>
                <p className="text-xl font-mono font-bold" data-testid="today-clock-in">{fmtTime(today?.clock_in)}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-slate-400">Clock Out</p>
                <p className="text-xl font-mono font-bold" data-testid="today-clock-out">{fmtTime(today?.clock_out)}</p>
              </div>
              <div className="flex flex-col gap-2">
                <Button data-testid="attendance-clock-in-button" className="bg-emerald-600 hover:bg-emerald-500" disabled={!!today?.clock_in} onClick={() => clock("in")}>
                  <LogIn className="w-4 h-4 mr-1" /> Clock In
                </Button>
                <Button data-testid="attendance-clock-out-button" variant="outline" className="border-slate-500 text-white hover:bg-slate-700"
                  disabled={!today?.clock_in || !!today?.clock_out} onClick={() => clock("out")}>
                  <LogOut className="w-4 h-4 mr-1" /> Clock Out
                </Button>
              </div>
            </div>
          </div>
          <p className="mt-4 text-xs text-slate-500 flex items-center gap-1">
            <MapPin className="w-3 h-3" /> {bm ? "Lokasi GPS hanya direkod semasa clock-in/out mengikut polisi syarikat." : "GPS location is only captured at clock-in/out events per company policy."}
          </p>
        </CardContent>
      </Card>

      <div className="bg-card border rounded-xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{bm ? "Tarikh" : "Date"}</TableHead>
              <TableHead>In</TableHead>
              <TableHead>Out</TableHead>
              <TableHead className="hidden md:table-cell">{bm ? "Jam Kerja" : "Hours"}</TableHead>
              <TableHead className="hidden md:table-cell">OT</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {records.map((r) => (
              <TableRow key={r.attendance_id} data-testid={`attendance-row-${r.date}`}>
                <TableCell className="text-sm font-medium font-mono">{r.date}</TableCell>
                <TableCell className="font-mono text-sm">{fmtTime(r.clock_in)}</TableCell>
                <TableCell className="font-mono text-sm">{fmtTime(r.clock_out)}</TableCell>
                <TableCell className="hidden md:table-cell font-mono text-sm">{r.work_hours ?? "-"}</TableCell>
                <TableCell className="hidden md:table-cell font-mono text-sm">{r.overtime_hours ? `${r.overtime_hours}h` : "-"}</TableCell>
                <TableCell><Badge variant="outline" className={STATUS_COLORS[r.status] || ""}>{r.status}</Badge></TableCell>
              </TableRow>
            ))}
            {records.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-sm text-slate-500 py-8">{bm ? "Tiada rekod." : "No records."}</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
